import { useLoaderData } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { RoccoLink } from '@rocco/components/link';
import { useItemAdminData } from '~/modules/directory/hook/use-item-admin-data';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import type { loader } from '~/routes/_app.product.$urlSegment';
import type { CategoryPill } from '~/ui-rocco/pages/home-page/components/CategoryPills';
import { CategoryPills } from '~/ui-rocco/pages/home-page/components/CategoryPills';
import {
    AboutProfessional,
    ReadMoreText,
    TileCarousel,
    type DetailTile,
    detailCta,
    stripHtml,
} from '~/ui-rocco/pages/detail-shared';
import { useProductEnquiryModal } from '../../hooks/use-product-enquiry-modal';
import { useEnquiryOwnsIsland } from '../../hooks/use-enquiry-owns-island';

/**
 * (NN) About the Supplier — per Figma 1841:10484 the supplier block lives
 * inside the spec accordion: giant name + logo, category pills beside the
 * summary and a View Profile CTA, then the professional's products as a
 * headerless carousel (the standalone "Our Products" heading is gone).
 *
 * Mobile renders the compact layout instead (name link, pills beside the
 * summary, full-bleed products carousel, stacked View Shop / Enquire pills) —
 * the accordion trigger supplies the heading, so the old standalone section's
 * hairline + eyebrow are gone.
 *
 * On desktop the products carousel is the section's only Magic Island
 * registration — it drives the island's pagination + auto-scroll. The profile
 * block deliberately registers no island: it is nearly a screen tall, so a
 * "Read more about" link there would hold the island across that whole block
 * and keep the carousel pagination from showing until you scrolled well past
 * the products. Mobile registers no island here at all — the persistent Enquire
 * pill stays put rather than being swapped for a nav link that pulls the reader
 * off the product (the "View Shop" / profile links are in the row itself).
 */
export const AboutSupplierRowContent = () => {
    const { product } = useLoaderData<typeof loader>();
    const { mobile } = useAppDisplayModeContext();
    const enquiryOwnsIsland = useEnquiryOwnsIsland();
    const { openEnquiryModal } = useProductEnquiryModal();
    const professional = product.Professional;
    // Enquire respects the professional's enquiry audience, like every other
    // enquiry CTA on the page.
    const { showEnquiryCTA } = useEnquiryVisibility(professional);

    const categories: CategoryPill[] = (product.ProductCategories ?? [])
        .slice(0, 5)
        .map((category) => ({
            label: category.PrettyTitle ?? '',
            to: category.Link ?? undefined,
        }))
        .filter((pill) => pill.label !== '');
    const summary = professional.BlockSummary
        ? stripHtml(professional.BlockSummary)
        : undefined;

    const alsoFromProducts = product.AlsoFromProducts?.products ?? [];
    const adminData = useItemAdminData(alsoFromProducts.map((tile) => tile.id));
    const tiles: DetailTile[] = alsoFromProducts.map((tile) => ({
        ...tile,
        pinType: 'ProductPin',
        adminData: adminData[tile.id],
        professional: { id: professional?.ID, name: professional?.Title },
        professionalLink: professional?.Link,
    }));

    if (mobile) {
        return (
            <div className="pb-4 text-[#1f1f21]">
                <div>
                    {/* text-[1.5rem] (24px per the frame) — this config remaps
                        text-2xl to 28px. */}
                    <h3 className="m-0 mt-1 font-sans text-[1.5rem] leading-[1.05] font-normal">
                        <RoccoLink to={professional.Link}>
                            {professional.Title}
                        </RoccoLink>
                    </h3>
                    <div className="mt-6 flex items-start gap-6">
                        {categories.length > 0 ? (
                            <CategoryPills
                                label="Popular categories:"
                                items={categories}
                                className="w-[9.1875rem] shrink-0"
                            />
                        ) : null}
                        {summary ? (
                            <div className="min-w-0 flex-1">
                                <ReadMoreText
                                    text={summary}
                                    lines={3}
                                    anchorHref={professional.Link}
                                    className="font-sans text-[0.8125rem] leading-snug text-[#1f1f21]"
                                    toggleClassName="mt-1 text-[0.8125rem]"
                                />
                            </div>
                        ) : null}
                    </div>
                </div>
                {tiles.length > 0 ? (
                    /* Full-bleed: -mx-5 cancels the accordion's px-5 gutter so
                       the viewport spans edge to edge, then a 1rem viewport
                       inset matches the hero gallery's peek gutter. Tiles
                       mirror the standard product tile (default 291/400 media),
                       roughly half the viewport so two show with a peek. */
                    <div className="-mx-5 mt-8 qhd:-mx-[calc((100vw_-_120rem)/2_+_3rem)]">
                        <TileCarousel
                            eyebrow=""
                            ariaLabel="More from this supplier"
                            tiles={tiles}
                            hideHeader
                            hidePagination
                            peek
                            // Above 1920 the row breaks out of the capped
                            // accordion: slides snap to the container gutter
                            // and run full-bleed to the viewport edge.
                            viewportClassName="qhd:px-[calc((100%_-_120rem)/2_+_3rem)]"
                        />
                    </div>
                ) : null}
                <div className="mt-8 flex flex-col gap-3">
                    <RoccoLink
                        to={`${professional.Link}/our-products`}
                        className={detailCta(
                            'filled-light',
                            'h-[3.125rem] w-full text-sm',
                        )}
                    >
                        View Shop
                    </RoccoLink>
                    {showEnquiryCTA ? (
                        <button
                            type="button"
                            onClick={() =>
                                openEnquiryModal({
                                    title: 'Enquire',
                                    ctaLabel: 'Enquire',
                                })
                            }
                            className={detailCta(
                                'outlined-light',
                                'h-[3.125rem] w-full text-sm',
                            )}
                        >
                            Enquire
                        </button>
                    ) : null}
                </div>
            </div>
        );
    }

    return (
        <div className="pb-[1.8rem]">
            <AboutProfessional
                hideHeader
                name={professional.Title}
                profileLink={professional.Link}
                logo={
                    professional.CompanyLogo
                        ? {
                              src: professional.CompanyLogo,
                              background: professional.LogoBackgroundColor,
                          }
                        : undefined
                }
                categories={categories}
                summary={summary}
                secondaryAction={
                    showEnquiryCTA
                        ? {
                              label: 'Enquire',
                              onClick: () =>
                                  openEnquiryModal({
                                      title: 'Enquire',
                                      ctaLabel: 'Enquire',
                                  }),
                          }
                        : undefined
                }
            />
            {tiles.length > 0 ? (
                /* Above 1920 the rail breaks out of the capped accordion:
                   slides snap to the container gutter and run full-bleed to
                   the viewport edge. */
                <div className="qhd:-mx-[calc((100vw_-_120rem)/2_+_3rem)] md:mt-[7rem]">
                    <TileCarousel
                        eyebrow=""
                        ariaLabel="More from this supplier"
                        tiles={tiles}
                        hideHeader
                        islandAutoplay
                        // Past half-scroll the island belongs to the enquiry
                        // fallback; this carousel claim runs in the first half.
                        islandClaimEnabled={!enquiryOwnsIsland}
                        // No fade-in: the row opens on reader intent, and the
                        // pre-reveal dim reads as missing tiles inside it.
                        revealAnimation={false}
                        islandLabel={`More from ${professional.Title}`}
                        cta={{
                            label: 'View more products',
                            to: `${professional.Link}/our-products`,
                        }}
                        revealThreshold={0.8}
                        viewportClassName="qhd:px-[calc((100%_-_120rem)/2_+_3rem)]"
                    />
                </div>
            ) : null}
        </div>
    );
};
